import {
  GoABadge,
  GoABlock,
  GoAButton,
  GoACheckbox,
  GoAChip,
  GoAContainer,
  GoADatePicker,
  GoADivider,
  GoADropdown,
  GoADropdownItem,
  GoAFilterChip,
  GoAFormItem,
  GoAIcon,
  GoAIconButton,
  GoAInput,
  GoAInputDate,
  GoAInputSearch,
  GoAPagination,
  GoASpacer,
  GoATab,
  GoATable,
  GoATabs,
  GoATooltip,
} from '@abgov/react-components';
import { useEffect, useRef, useState } from 'react';
import { RootState, AppDispatch } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  Agency,
  District,
  fetchLawEnforcementConfiguration,
} from '../../state/slices/lawEnforcementConfigSlice';
import useCsrService from '../../services/csr-api-redux';
import {
  clearLECollision,
  fetchLawEnforcementCollisionsByAgency,
  Filter,
} from '../../state/slices/lawEnforcementCollisionsSlice';
import { Collision } from '../../models/collision';
import moment from 'moment';
import styles from './law-enforcement.module.css';
import { formatDateToISOWithTimezone } from '../../common/validation';
import { useNavigate } from 'react-router-dom';
import { getReportStatus, getViolations } from './law-enforcement-common';
import {
  isAdmin,
  isLawEnforcementAdmin,
  isLawEnforcementConfigured,
  isLawEnforcementOfficer,
  isUserLoggedIn,
  loadCsrUser,
} from '../../state/slices/authSlice';
import { fetchConfiguration } from '../../state/slices/configurationSlice';
import {
  getAllLEOfficers,
  getMyLEUser,
  Reviewer,
} from '../../state/slices/lawEnforcementOfficersSlice';
import { NotificationType } from '../notifications/notification-component';
import { pushNotification } from '../../state/slices/notificationSlice';
import { stat } from 'fs';
import {
  fetchAllAgreements,
  fetchLatestUserAgreementAccepted,
  fetchUserAgreements,
} from '../../state/slices/agreementSlice';
import { s } from 'vite/dist/node/types.d-aGj9QkWt';

interface LawEnforcementDashboardProps {}

export const LawEnforcementDashboardComponent =
  ({}: LawEnforcementDashboardProps) => {
    const useAppSelector: <TSelected>(
      selector: (state: RootState) => TSelected,
    ) => TSelected = useSelector;

    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const stateAuth = useAppSelector((state: RootState) => state.auth);
    const csrService = useCsrService();
    const stateLawEnforcementConfiguration = useAppSelector(
      (state: RootState) => state.lawEnfocementConfiguration,
    );
    const stateLawEnforcementCollisions = useAppSelector(
      (state: RootState) => state.lawEnforcementCollisions,
    );
    const stateLawEnforcementLEOfficers = useAppSelector(
      (state: RootState) => state.lawEnforcementLEOfficers,
    );

    const stateUserAgreements = useAppSelector(
      (state: RootState) => state.userAgreements,
    );

    const defaultAgency = 'CPS';

    const [filter, setFilter] = useState<Filter>({
      agencies: [
        isAdmin(stateAuth) ? defaultAgency : (stateLawEnforcementLEOfficers.user?.agency || defaultAgency)
      ],
    });

    const [collisions, setCollisions] = useState<Collision[]>([]);
    const [reportStatusToShow, setReportStatusToShow] = useState([2]);
    const [unassigned, setUnassigned] = useState(0);
    const [underReview, setUnderReview] = useState(0);
    const [completed, setCompleted] = useState(0);
    const [searchField, setSearchField] = useState('surname');
    const [search, setSearch] = useState('');
    const [searchInputValue, setSearchInputValue] = useState('');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);
    const [hasFetchedOfficers, setHasFetchedOfficers] = useState<{[key: string]: boolean}>({});
    const [agencyReviewers, setAgencyReviewers] = useState<{[key: string]: Reviewer[]}>({});

    useEffect(() => {
      if (!stateAuth.auth.access.access) navigate('/');
      if (!stateAuth.auth.csrUser && !stateAuth.isLoading) {
        dispatch(loadCsrUser({ csrService }));
        return;
      }

      if (stateAuth.isLoading || !stateAuth.auth.csrUser) return;
      else {
        if (
          stateAuth.auth.csrUser &&
          !isAdmin(stateAuth) &&
          !isLawEnforcementAdmin(stateAuth) &&
          !isLawEnforcementOfficer(stateAuth)
        ) {
          dispatch(
            pushNotification({
              body: 'You do not have permission to view this page.',
              notificationType: NotificationType.importantGenericError,
            }),
          );
          navigate('/');
        }

        if (
          stateAuth.auth.csrUser &&
          (isLawEnforcementOfficer(stateAuth) ||
            isLawEnforcementAdmin(stateAuth)) &&
          !isLawEnforcementConfigured(stateAuth)
        ) {
          dispatch(
            pushNotification({
              body: 'Contact TEC admin to configure your law enforcement account.',
              notificationType: NotificationType.importantGenericError,
            }),
          );
        }

        if (
          stateAuth.auth.csrUser &&
          isLawEnforcementOfficer(stateAuth) &&
          !stateUserAgreements.latestUserAgreementAccepted
        ) {
          navigate('/law/tos');
        }
      }
    }, [
      stateAuth.auth.csrUser,
      stateUserAgreements.latestUserAgreementAccepted,
      stateAuth.isLoading,
      navigate,
    ]);

    useEffect(() => {
      dispatch(fetchLatestUserAgreementAccepted(csrService));
    }, []);

    useEffect(() => {
      dispatch(fetchConfiguration(csrService));
    }, []);

    useEffect(() => {
      if (stateAuth.auth.csrUser) {
        dispatch(getMyLEUser({ csrService }));
      }
    }, [stateAuth.auth.csrUser]);

    useEffect(() => {
      if (stateLawEnforcementLEOfficers.user) {
        // For non-admin users, always use their assigned agency
        const agencyToFetch = isAdmin(stateAuth) ?
          (filter.agencies?.[0] || defaultAgency) :
          stateLawEnforcementLEOfficers.user.agency;

        dispatch(
          fetchLawEnforcementConfiguration({
            agency: agencyToFetch,
            csrService,
          }),
        );
      }
    }, [stateLawEnforcementLEOfficers.user?.agency, filter.agencies]);

    useEffect(() => {
      const fetchDataAsync = async () => {
        if (
          stateAuth.auth.csrUser &&
          stateLawEnforcementConfiguration.configuration.agencies &&
          stateLawEnforcementConfiguration.configuration.agencies.length > 0
        ) {
          if (isUserLoggedIn(stateAuth)) {
            try {
              // For non-admin users, ensure they can only see their agency's collisions
              const filterToUse = isAdmin(stateAuth) ?
                { ...filter, agencies: filter.agencies?.length ? filter.agencies : [defaultAgency] } :
                {
                  ...filter,
                  agencies: [stateLawEnforcementLEOfficers.user?.agency || defaultAgency]
                };

              await dispatch(
                fetchLawEnforcementCollisionsByAgency({ filter: filterToUse, csrService }),
              );
            } catch (error: any) {
              console.error(error);
            }
          }
        }
      };

      fetchDataAsync();
    }, [
      stateAuth.auth.csrUser,
      stateLawEnforcementConfiguration.configuration,
      filter,
      stateLawEnforcementLEOfficers.user
    ]);

    useEffect(() => {
      if (stateLawEnforcementCollisions.collisions) {
        let filteredList: Collision[] = [];

        filteredList = stateLawEnforcementCollisions.collisions
          .filter((collision: Collision) =>
            reportStatusToShow.includes(collision.reportStatus!),
          )
          .filter((collision: Collision) => applySearchFilter(collision));

        setCollisions(filteredList);
        setPage(1);
      }
    }, [stateLawEnforcementCollisions.collisions, reportStatusToShow]);

    useEffect(() => {
      if (collisions && collisions.length > 0) {
        setUnassigned(collisions.filter((c) => c.reportStatus === 2).length);
        setUnderReview(collisions.filter((c) => c.reportStatus === 3).length);
        setCompleted(
          collisions.filter((c) => c.reportStatus === 4 || c.reportStatus === 5)
            .length,
        );
      } else {
        setUnassigned(0);
        setUnderReview(0);
        setCompleted(0);
      }
    }, [collisions]);

    const handleChangeTab = (tabIndex: number) => {
      if (tabIndex === 1) {
        setReportStatusToShow([2]);
      }
      if (tabIndex === 2) {
        setReportStatusToShow([3]);
      }
      if (tabIndex === 3) {
        setReportStatusToShow([4, 5]);
      }
      setPage(1);
    };

    const applySearchFilter = (collision: Collision): boolean => {
      if (search) {
        if (
          searchField === 'surname' &&
          collision.vehicles![0].driver &&
          collision.vehicles![0].driver.lastName
        ) {
          return collision
            .vehicles![0].driver!.lastName.toLowerCase()
            .includes(search.toLowerCase());
        }
        if (
          searchField === 'platenumber' &&
          collision.vehicles![0].licencePlateNumber
        ) {
          return collision
            .vehicles![0].licencePlateNumber!.toLowerCase()
            .includes(search.toLowerCase());
        }
        if (searchField === 'filenumber' && collision.policeFileNumber) {
          return collision
            .policeFileNumber!.toLowerCase()
            .includes(search.toLowerCase());
        }
        if (searchField === 'csrid' && collision.id) {
          return collision
            .id!.toString()
            .toLowerCase()
            .includes(search.toLowerCase());
        }
      }

      if (!search || search === null || search === '') {
        return true;
      }

      return false;
    };

    useEffect(() => {
      dispatch(fetchLawEnforcementCollisionsByAgency({ filter, csrService }));
    }, [filter]);

    const getFilterCount = (): number => {
      let filterCount = 1; // we always apply the base agency filter

      if (filter.startDate) {
        filterCount++;
      }
      if (filter.endDate) {
        filterCount++;
      }

      if (filter.reviewer) {
        filterCount++;
      }

      if (filter.locations && filter.locations.length > 0) {
        filterCount++;
      }

      return filterCount;
    };

    return (
      <div>
        <SideDrawer
          title="Filter collision reports"
          isOpen={showFilterDrawer}
          onClose={() => {
            setShowFilterDrawer(false);
          }}
          agencies={
            stateLawEnforcementConfiguration.configuration.agencies &&
            stateLawEnforcementConfiguration.configuration.agencies.length > 0
              ? stateLawEnforcementConfiguration.configuration.agencies.map(
                  (agency) => agency.code,
                )
              : []
          }
          locations={
            stateLawEnforcementConfiguration.configuration.agencies &&
            stateLawEnforcementConfiguration.configuration.agencies.length > 0
              ? stateLawEnforcementConfiguration.configuration.agencies
                  .flatMap(agency => agency.districts.map(district => ({
                    ...district,
                    agency_code: agency.code
                  })))
              : []
          }
          reviewers={
            stateLawEnforcementLEOfficers.reviewers
              ? stateLawEnforcementLEOfficers.reviewers
              : []
          }
          officerAccount={stateLawEnforcementLEOfficers.user}
          isAdmin={isAdmin(stateAuth)}
          defaultAgency={
            (() => {
              const agencies = stateLawEnforcementConfiguration.configuration.agencies || [];
              const agencyCode = stateLawEnforcementLEOfficers.user?.agency
                ? stateLawEnforcementLEOfficers.user.agency
                : defaultAgency;
              const selectedAgency = agencies.find((agency) => agency.code === agencyCode) || agencies.find((agency) => agency.code === defaultAgency)!;
              return selectedAgency;
            })()
          }
          setFilter={({
            startDate,
            endDate,
            locations,
            agencies,
            reviewer,
          }) => {
            setFilter({
              startDate,
              endDate,
              locations,
              agencies,
              reviewer,
            });
          }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            maxWidth: '1216px',
          }}
        >
          <h2>Collision Reports</h2>
          <GoABlock mt={'2xl'} alignment="center">
            <GoADropdown
              value={searchField}
              onChange={(item, value) => {
                setSearchInputValue('');
                setSearch('');
                setSearchField(value.toString());
              }}
              ml={'2xl'}
              width="20ch"
            >
              <GoADropdownItem
                value="surname"
                label="Surname"
              ></GoADropdownItem>
              <GoADropdownItem
                value="platenumber"
                label="Plate number"
              ></GoADropdownItem>
              <GoADropdownItem
                value="filenumber"
                label="Police file number"
              ></GoADropdownItem>
              <GoADropdownItem value="csrid" label="RMC ID"></GoADropdownItem>
            </GoADropdown>
            <GoAInput
              leadingIcon="search"
              name="searchInput"
              value={searchInputValue}
              onChange={(item, value) => {
                setSearchInputValue(value);
              }}
            ></GoAInput>
            <GoAButton
              onClick={() => {
                if (searchInputValue != '') {
                  setReportStatusToShow([2, 3, 4, 5]);
                  setSearch(searchInputValue);
                }
              }}
            >
              Search
            </GoAButton>
            <GoAButton
              type="secondary"
              trailingIcon="funnel"
              onClick={() => {
                setShowFilterDrawer(true);
              }}
            >
              Filters ({getFilterCount()})
            </GoAButton>
          </GoABlock>
        </div>
        {search ? (
          <div>
            <GoASpacer vSpacing={'3xl'} />
            <div style={{ display: 'flex' }}>
              <span>
                <h3>Search results for "{search}"</h3>
              </span>
              <span>
                <GoAButton
                  type="tertiary"
                  mb={'xs'}
                  onClick={() => {
                    setReportStatusToShow([2]);
                    setSearchInputValue('');
                    setSearch('');
                  }}
                >
                  Clear{' '}
                </GoAButton>
              </span>
            </div>
            <GoASpacer vSpacing={'3xl'} />
          </div>
        ) : (
          <div>
            <GoASpacer vSpacing={'2xl'} />
            <GoATabs onChange={(tab) => handleChangeTab(tab)} initialTab={1}>
              <GoATab
                key="unassigned"
                heading={
                  <div>
                    Unassigned{' '}
                    {unassigned && unassigned != 0 ? (
                      <GoABadge
                        type="information"
                        content={unassigned.toString()}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                }
              ></GoATab>
              <GoATab
                heading={
                  <div>
                    Under review{' '}
                    {underReview && underReview != 0 ? (
                      <GoABadge
                        type="information"
                        content={underReview.toString()}
                      />
                    ) : (
                      ''
                    )}
                  </div>
                }
              ></GoATab>
              <GoATab heading={<div>Completed</div>}></GoATab>
            </GoATabs>
          </div>
        )}
        <GoATable width="1216px">
          <thead>
            <tr>
              <th>RMC ID</th>
              <th>Collision date</th>
              <th>Licence plate</th>
              <th>Driver surname</th>
              <th>Report status</th>
              <th>Reviewer</th>
              <th>Police file number</th>
              <th>Violation</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {collisions
              .sort((a, b) => {
                return (
                  new Date(a.dateOfCollision!).getTime() -
                  new Date(b.dateOfCollision!).getTime()
                );
              })
              .slice((page - 1) * perPage, page * perPage)
              .map((collision) => (
                <tr key={collision.id}>
                  <td>{collision.id}</td>
                  <td>
                    {moment(collision.dateOfCollision!).format('YYYY-MM-DD')}
                  </td>
                  <td>{collision.vehicles![0].licencePlateNumber}</td>
                  <td>{collision.vehicles![0].driver!.lastName}</td>
                  <td>
                    {getReportStatus(
                      collision.reportStatus!,
                      new Date(collision.reportedDate!),
                    )}
                  </td>
                  <td>
                    {
                      stateLawEnforcementLEOfficers.reviewers?.find(
                        (x: Reviewer) =>
                          x.badgeNumber &&
                          x.badgeNumber.toString() ==
                            collision.reviewerRegNumber?.toString(),
                      )?.name
                    }
                  </td>
                  <td>{collision.policeFileNumber}</td>
                  <td>
                    {getViolations(collision).length > 0 ? (
                      <GoATooltip
                        content={
                          getViolations(collision).length > 1
                            ? 'Multiple Violaitions'
                            : getViolations(collision)[0]
                        }
                      >
                        <GoAIcon type="flag" fillColor="red" theme="filled" />
                      </GoATooltip>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    <GoAButton
                      type="tertiary"
                      onClick={() => {
                        dispatch(clearLECollision());
                        navigate(`/law/collision/${collision.id}`);
                      }}
                    >
                      View
                    </GoAButton>
                  </td>
                </tr>
              ))}
          </tbody>
        </GoATable>
        <GoASpacer vSpacing={'l'}></GoASpacer>
        <div style={{ maxWidth: '1216px' }}>
          {collisions.length > 0 ? (
            <GoABlock alignment="center">
              <GoABlock mb="m" alignment="center">
                Show
                <GoADropdown
                  onChange={(item, value) => {
                    if (
                      value &&
                      parseInt(value.toString()) > collisions.length
                    ) {
                      setPage(1);
                    }

                    setPerPage(parseInt(value.toString()));
                  }}
                  value={perPage.toString()}
                  width="10ch"
                  relative={true}
                >
                  <GoADropdownItem value="20" label="20"></GoADropdownItem>
                  <GoADropdownItem value="30" label="30"></GoADropdownItem>
                  <GoADropdownItem value="40" label="40"></GoADropdownItem>
                  <GoADropdownItem value="50" label="50"></GoADropdownItem>
                  <GoADropdownItem value="60" label="60"></GoADropdownItem>
                </GoADropdown>
                <span style={{ width: '50px' }}>
                  of{' '}
                  {
                    collisions.filter((collision) =>
                      reportStatusToShow.includes(collision.reportStatus!),
                    ).length
                  }
                </span>
              </GoABlock>

              <GoASpacer hSpacing="fill"></GoASpacer>

              <GoAPagination
                itemCount={
                  collisions.filter((collision) =>
                    reportStatusToShow.includes(collision.reportStatus!),
                  ).length
                }
                perPageCount={perPage}
                onChange={(pageNumber) => {
                  setPage(pageNumber);
                }}
                pageNumber={page}
              />
            </GoABlock>
          ) : (
            <GoABlock alignment="center" direction="column">
              <GoASpacer vSpacing={'xl'} />
              <GoABlock alignment="center">
                <div
                  style={{
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#EFF8FF',
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex',
                  }}
                >
                  <GoAIcon
                    type="file-tray"
                    theme="outline"
                    fillColor="blue"
                    size="large"
                  />
                </div>
              </GoABlock>
              <div
                style={{
                  font: 'var(--goa-typography-body-s)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <div>
                  There are no results that match your search and filter
                  criteria.
                </div>
                <div>
                  Try adjusting your filters, or using a different search term.
                </div>
              </div>
            </GoABlock>
          )}
        </div>
      </div>
    );
  };

interface SideDrawerProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  agencies: string[];
  locations: District[];
  reviewers: Reviewer[];
  officerAccount?: Reviewer;
  defaultAgency: Agency;
  isAdmin: boolean;
  setFilter: ({
    startDate,
    endDate,
    locations,
    agencies,
    reviewer,
  }: Filter) => void;
}

const SideDrawer: React.FC<SideDrawerProps> = ({
  title,
  isOpen,
  onClose,
  agencies = [],
  locations,
  reviewers,
  officerAccount,
  setFilter,
  defaultAgency,
  isAdmin,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const csrService = useCsrService();
  const [locationSelection, setLocationSelection] = useState<string>();
  const [locationSelections, setLocationSelections] = useState<string[]>([]);
  const [startDateSelection, setStartDateSelection] = useState<Date>();
  const [endDateSelection, setEndDateSelection] = useState<Date>();
  const [agencySelection, setAgencySelection] = useState<string>();
  const [reviewerSelection, setReviewerSelection] = useState<string>('');
  const [filteredLocations, setFilteredLocations] = useState<District[]>([]);
  const [filteredReviewers, setFilteredReviewers] = useState<Reviewer[]>([]);
  const [hasFetchedOfficers, setHasFetchedOfficers] = useState<{[key: string]: boolean}>({});
  const [agencyReviewers, setAgencyReviewers] = useState<{[key: string]: Reviewer[]}>({});

  const validAgencies = Array.isArray(agencies) ? agencies : [];

  useEffect(() => {
    if (isOpen) {
      if (isAdmin) {
        if (!agencySelection) {
          setAgencySelection(defaultAgency.code);
        }
      } else if (defaultAgency || officerAccount) {
        const agency = officerAccount ? officerAccount.agency : defaultAgency.code;
        setAgencySelection(agency);
      }
    }
  }, [isOpen, isAdmin, officerAccount, defaultAgency]);

  useEffect(() => {
    if (agencySelection) {
      console.log('Agency selection changed:', agencySelection);
      const agencyLocations = locations.filter(
        (location) => location.agency_code === agencySelection
      );
      setFilteredLocations(agencyLocations);

      // Clear selections when changing agencies
      if (agencySelection !== defaultAgency.code) {
        setLocationSelections([]);
        setLocationSelection('');
        setReviewerSelection('');
      }

      // Fetch officers when agency changes
      console.log(`Fetching officers for agency: ${agencySelection}`);
      dispatch(getAllLEOfficers({ agency: agencySelection, csrService }));
    }
  }, [agencySelection]);

  useEffect(() => {
    if (reviewers && agencySelection) {
      console.log(`Updating reviewers for agency: ${agencySelection}`);
      console.log('Current reviewers:', reviewers);

      // Store reviewers for the current agency
      const currentAgencyReviewers = reviewers.filter(reviewer => reviewer.agency === agencySelection);
      setAgencyReviewers(prev => ({...prev, [agencySelection as string]: currentAgencyReviewers}));

      // Set filtered reviewers for the current agency
      setFilteredReviewers(currentAgencyReviewers);
      console.log('Filtered reviewers:', currentAgencyReviewers);

      // Always clear reviewer selection when agency changes
      setReviewerSelection('');
    }
  }, [reviewers, agencySelection]);

  const handleApplyFilter = () => {
    const filterData = {
      startDate: startDateSelection ? formatDateToISOWithTimezone(startDateSelection) : undefined,
      endDate: endDateSelection ? formatDateToISOWithTimezone(endDateSelection) : undefined,
      locations: locationSelections || [],
      agencies: agencySelection ? [agencySelection] : [defaultAgency.code],
      reviewer: reviewerSelection || '',
    };
    console.log('Applying filter with dates:', {
      startDate: filterData.startDate,
      endDate: filterData.endDate,
      agency: filterData.agencies[0],
      locations: filterData.locations,
      rawStartDate: startDateSelection,
      rawEndDate: endDateSelection
    });
    setFilter(filterData);
    onClose();
  };

  const handleResetFilter = () => {
    setLocationSelections([]);
    setFilteredLocations([]);
    setLocationSelection('');
    setReviewerSelection('');
    setStartDateSelection(undefined);
    setEndDateSelection(undefined);

    if (isAdmin) {
      setAgencySelection(defaultAgency.code);
      setFilter({
        agencies: [defaultAgency.code],
      });
    } else {
      const defaultAgencyCode = officerAccount ? officerAccount.agency : defaultAgency.code;
      setAgencySelection(defaultAgencyCode);
      setFilter({
        agencies: [defaultAgencyCode],
      });
    }
  };

  return (
    <>
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(051,51,51, 0.5)',
            zIndex: 999,
          }}
        >
          <div
            className="drawer"
            style={{
              position: 'fixed',
              top: 0,
              right: isOpen ? 0 : '-300px',
              width: '450px',
              height: '100vh',
              backgroundColor: 'white',
              transition: 'right 0.3s ease',
              zIndex: 1000,
              padding: '32px',
              boxShadow: '-4px 4 4px rgba(0, 0, 0, 0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h3>{title}</h3>{' '}
              <GoAIconButton
                icon="close"
                onClick={onClose}
                size="large"
                variant="dark"
              />
            </div>
            <div style={{ flexGrow: 1, overflowY: 'scroll', height: '100%' }}>
              <GoASpacer vSpacing={'l'} />
              <h4>Filter by agency</h4>
              <GoASpacer vSpacing={'s'} />
              <div>
                <GoADropdown
                  name="agency"
                  value={agencySelection || ''}
                  disabled={!isAdmin}
                  width="334px"
                  placeholder={agencySelection ? "1 agency selected" : "0 agencies selected"}
                  onChange={(name, value) => {
                    setAgencySelection(value.toString());
                  }}
                >
                  {validAgencies.map((agency) => (
                    <GoADropdownItem
                      value={agency}
                      label={agency}
                      key={agency}
                    />
                  ))}
                </GoADropdown>
              </div>
              <GoASpacer vSpacing={'s'} />
              <div style={{ font: 'var(--goa-typography-body-s)' }}>
                {isAdmin
                  ? "Select an agency to filter collision reports."
                  : "This filter is applied automatically by an administrator and cannot be disabled."}
              </div>
              <GoASpacer vSpacing={'l'} />
              <GoADivider />
              <GoASpacer vSpacing={'l'} />
              <h4>Filter by location</h4>
              <GoASpacer vSpacing={'s'} />
              <GoADropdown
                width="334px"
                filterable={true}
                value={locationSelection}
                placeholder={`${locationSelections.length} locations selected`}
                onChange={(item, value) => {
                  if (value)
                    if (!locationSelections.includes(value.toString())) {
                      setLocationSelections([
                        ...locationSelections,
                        value.toString(),
                      ]);
                      setLocationSelection(value.toString());
                    }
                }}
              >
                {filteredLocations
                  .filter(location => !locationSelections.includes(location.code))
                  .map((location) => (
                    <GoADropdownItem
                      mountType={'reset'}
                      value={location.code}
                      label={location.description}
                      key={location.code}
                    />
                  ))}
              </GoADropdown>
              <GoASpacer vSpacing={'s'} />
              <div>
                {locationSelections.map((location) => {
                  const locationDescription = filteredLocations.find(
                    (d) => d.code === location,
                  )?.description;

                  return (
                    <span key={location}>
                      <GoAFilterChip
                        content={locationDescription || location}
                        iconTheme="outline"
                        mr={'xs'}
                        mb={'xs'}
                        onClick={() => {
                          setLocationSelections(
                            locationSelections.filter((l) => l !== location),
                          );
                        }}
                      />
                    </span>
                  );
                })}
              </div>
              <GoASpacer vSpacing={'l'} />
              <GoADivider />
              <GoASpacer vSpacing={'l'} />
              <h4>Filter by reviewer</h4>
              <GoASpacer vSpacing={'s'} />
              <GoADropdown
                width="334px"
                placeholder="Select reviewer"
                value={reviewerSelection}
                onChange={(item, value) => {
                  setReviewerSelection(value.toString());
                }}
              >
                {filteredReviewers.map((reviewer) => (
                  <GoADropdownItem
                    value={reviewer.badgeNumber}
                    label={reviewer.name}
                    key={reviewer.badgeNumber}
                  />
                ))}
              </GoADropdown>
              <GoASpacer vSpacing={'l'} />
              <GoADivider />
              <GoASpacer vSpacing={'l'} />
              <h4>From date</h4>
              <GoASpacer vSpacing={'s'} />
              <GoAFormItem>
                <div style={{ width: '300px' }}>
                  <GoADatePicker
                    value={startDateSelection}
                    onChange={(item, value) => {
                      setStartDateSelection(value);
                    }}
                  />
                </div>
              </GoAFormItem>
              <GoASpacer vSpacing={'m'} />
              <h4>To date</h4>
              <GoASpacer vSpacing={'s'} />
              <GoAFormItem>
                <div style={{ width: '300px' }}>
                  <GoADatePicker
                    value={endDateSelection}
                    disabled={startDateSelection === undefined}
                    onChange={(item, value) => {
                      setEndDateSelection(value);
                    }}
                  />
                </div>
              </GoAFormItem>
              <GoASpacer vSpacing={'l'} />
              <GoADivider />
              <GoASpacer vSpacing={'l'} />
              <div className={styles.drawerapplybutton}>
                <GoABlock direction="row" gap="m">
                  <GoAButton onClick={handleApplyFilter}>
                    Apply Filters
                  </GoAButton>
                  <GoAButton
                    type="tertiary"
                    onClick={handleResetFilter}
                  >
                    Reset Filters
                  </GoAButton>
                </GoABlock>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
